import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { usePatrol } from '@/entities/patrol'
import { useSite } from '@/entities/site/model/queries'
import { useGuardLookup } from '@/entities/employee/model/queries'
import { useCheckpointTags } from '@/entities/patrol-tag'
import type { CheckpointTagResponse } from '@/entities/patrol-tag'
import {
  EXECUTION_STATUS_FILTER_OPTIONS,
  executionStatusLabel,
  usePatrolExecutions,
} from '@/entities/patrol-execution'
import type { ExecutionStatus } from '@/entities/patrol-execution'
import { usePatrolScans } from '@/entities/patrol-scan'
import {
  ExecutionBreakdownSheet,
  ExecutionStatusBadge,
  ScanFilters,
  ScanList,
} from '@/features/patrol-activity'
import type { ScanFilterValue } from '@/features/patrol-activity'
import { PageHeader } from '@/shared/components/layout/PageHeader'
import { Pagination } from '@/shared/components/Pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { Badge } from '@/shared/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { formatInSiteZone, tzAbbrev } from '@/shared/lib/siteTime'

const PAGE_SIZE = 20

const EXECUTION_STATUSES = [
  'Pending',
  'InProgress',
  'Completed',
  'Missed',
  'Incomplete',
  'Expired',
] as const

const searchSchema = z.object({
  tab: z.enum(['overview', 'executions', 'scans', 'checkpoints']).optional(),
  guardUserId: z.coerce.number().optional(),
  tagId: z.coerce.number().optional(),
  fromDate: z.string().optional(),
  toDate: z.string().optional(),
  status: z
    .enum(EXECUTION_STATUSES)
    .optional()
    .transform((status) => (status === 'Expired' ? undefined : status)),
})

export const Route = createFileRoute('/patrols/$patrolId/')({
  component: PatrolDetailPage,
  staticData: { authorize: true },
  validateSearch: searchSchema,
})

function PatrolDetailPage() {
  const { patrolId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const id = Number(patrolId)

  const patrol = usePatrol(id)
  const site = useSite(patrol.data?.siteId ?? 0)

  if (patrol.isLoading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    )
  }
  if (!patrol.data) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Patrol not found.
      </div>
    )
  }

  const timeZone = site.data?.timeZone
  const tab = search.tab ?? 'overview'
  const setTab = (next: string) =>
    navigate({
      search: (s) => ({
        ...s,
        tab: next as typeof search.tab,
      }),
    })

  return (
    <div className="flex h-full flex-col">
      <div className="px-4 pt-4 pb-2">
        <PageHeader
          title={patrol.data.title}
          breadcrumbs={[
            { label: 'Patrols' },
            { label: patrol.data.title },
          ]}
        />
      </div>
      <div className="border-b px-4 py-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            {patrol.data.scheduleType}
          </span>
          {site.data && (
            <span className="text-muted-foreground">
              · {site.data.siteName}
            </span>
          )}
          <Badge variant={patrol.data.isDraft ? 'secondary' : 'default'}>
            {patrol.data.isDraft ? 'Draft' : 'Published'}
          </Badge>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex flex-1 flex-col">
        <TabsList className="mx-4 mt-2 w-fit">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="executions">Executions</TabsTrigger>
          <TabsTrigger value="scans">Scans</TabsTrigger>
          <TabsTrigger value="checkpoints">Checkpoints</TabsTrigger>
        </TabsList>
        <div className="flex-1 overflow-auto p-4">
          <TabsContent value="overview">
            <PatrolOverview patrol={patrol.data} timeZone={timeZone} />
          </TabsContent>
          <TabsContent value="executions">
            <PatrolExecutionsTab
              patrolId={id}
              statusFilter={search.status}
              timeZone={timeZone}
              onStatusChange={(status) =>
                navigate({ search: (s) => ({ ...s, status }) })
              }
            />
          </TabsContent>
          <TabsContent value="scans">
            <PatrolScansTab
              patrolId={id}
              siteId={patrol.data.siteId}
              tagIds={patrol.data.tagIds ?? []}
              clientId={site.data?.clientId}
              timeZone={timeZone}
              initialFilter={{
                guardUserId: search.guardUserId,
                tagId: search.tagId,
                fromDate: search.fromDate,
                toDate: search.toDate,
              }}
              onFilterChange={(v) =>
                navigate({
                  search: (s) => ({
                    ...s,
                    guardUserId: v.guardUserId,
                    tagId: v.tagId,
                    fromDate: v.fromDate,
                    toDate: v.toDate,
                  }),
                })
              }
            />
          </TabsContent>
          <TabsContent value="checkpoints">
            <PatrolCheckpoints
              siteId={patrol.data.siteId}
              tagIds={patrol.data.tagIds ?? []}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

function formatStart(iso: string, timeZone: string | undefined): string {
  if (Number.isNaN(Date.parse(iso))) return iso
  const zone = tzAbbrev(timeZone, iso)
  const stamp = formatInSiteZone(iso, timeZone, 'MMM d, yyyy HH:mm')
  return zone ? `${stamp} ${zone}` : stamp
}

function PatrolOverview({
  patrol,
  timeZone,
}: {
  patrol: NonNullable<ReturnType<typeof usePatrol>['data']>
  timeZone?: string
}) {
  return (
    <div className="space-y-4 text-sm">
      <section>
        <h3 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
          Schedule
        </h3>
        <p>{patrol.scheduleType}</p>
        {patrol.scheduledStartTime && (
          <p className="text-xs text-muted-foreground">
            Starts: {formatStart(patrol.scheduledStartTime, timeZone)}
          </p>
        )}
        {patrol.repeatIntervalHours != null && (
          <p className="text-xs text-muted-foreground">
            Repeats every {patrol.repeatIntervalHours} hours
          </p>
        )}
      </section>
      <section>
        <h3 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
          Limits
        </h3>
        <p>Max duration: {patrol.maxDurationMinutes} min</p>
        <p>Reminder: {patrol.reminderBeforeMinutes} min before</p>
      </section>
      {patrol.description && (
        <section>
          <h3 className="mb-1 text-xs font-medium uppercase text-muted-foreground">
            Description
          </h3>
          <p>{patrol.description}</p>
        </section>
      )}
    </div>
  )
}

function PatrolExecutionsTab({
  patrolId,
  statusFilter,
  timeZone,
  onStatusChange,
}: {
  patrolId: number
  statusFilter?: ExecutionStatus
  timeZone?: string
  onStatusChange: (status: ExecutionStatus | undefined) => void
}) {
  const [pageNumber, setPageNumber] = useState(1)
  const [openExecutionId, setOpenExecutionId] = useState<number | null>(null)
  const executions = usePatrolExecutions(patrolId, {
    status: statusFilter,
    pageNumber,
    pageSize: PAGE_SIZE,
  })
  const items = executions.data?.items ?? []
  const totalPages = executions.data?.totalPages ?? 1
  const zone = tzAbbrev(timeZone)
  const opened = items.find((e) => e.executionId === openExecutionId)

  const changeStatus = (value: string) => {
    setPageNumber(1)
    onStatusChange(value === 'all' ? undefined : (value as ExecutionStatus))
  }

  return (
    <div className="space-y-3">
      <Select value={statusFilter ?? 'all'} onValueChange={changeStatus}>
        <SelectTrigger className="h-8 w-44 text-xs">
          <SelectValue placeholder="Outcome" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All outcomes</SelectItem>
          {EXECUTION_STATUS_FILTER_OPTIONS.map((status) => (
            <SelectItem key={status} value={status}>
              {executionStatusLabel(status)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {executions.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {statusFilter ? 'No runs with this outcome.' : 'No executions yet.'}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Scheduled{zone && ` (${zone})`}</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Finished</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Checkpoints</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => (
                <TableRow
                  key={e.executionId}
                  tabIndex={0}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setOpenExecutionId(e.executionId)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault()
                      setOpenExecutionId(e.executionId)
                    }
                  }}
                >
                  <TableCell className="text-sm">
                    {e.scheduledStartAt
                      ? formatInSiteZone(e.scheduledStartAt, timeZone, 'MMM d, HH:mm')
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {e.startedAt ? formatInSiteZone(e.startedAt, timeZone, 'HH:mm:ss') : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {e.completedAt ? formatInSiteZone(e.completedAt, timeZone, 'HH:mm:ss') : '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    <ExecutionStatusBadge status={e.status} />
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {e.checkpointsScanned != null && e.checkpointsTotal != null
                      ? `${e.checkpointsScanned} of ${e.checkpointsTotal}`
                      : e.scanCount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {totalPages > 1 && (
        <Pagination
          currentPage={pageNumber}
          totalPages={totalPages}
          onPageChange={setPageNumber}
          hasNextPage={pageNumber < totalPages}
          hasPreviousPage={pageNumber > 1}
        />
      )}
      <ExecutionBreakdownSheet
        patrolId={patrolId}
        executionId={openExecutionId}
        scheduledStartAt={opened?.scheduledStartAt}
        timeZone={timeZone}
        onClose={() => setOpenExecutionId(null)}
      />
    </div>
  )
}

function usePatrolTags(siteId: number, tagIds: number[]) {
  const tags = useCheckpointTags({ siteId, pageNumber: 1, pageSize: 1000 })
  const byId = useMemo(() => {
    const map = new Map<number, CheckpointTagResponse>()
    for (const tag of tags.data?.items ?? []) map.set(tag.id, tag)
    return map
  }, [tags.data])
  const options = useMemo(
    () =>
      tagIds.flatMap((tid) => {
        const tag = byId.get(tid)
        return tag ? [{ tagId: tid, label: tag.name }] : []
      }),
    [byId, tagIds],
  )
  return { byId, options, isLoading: tags.isLoading }
}

function PatrolScansTab({
  patrolId,
  siteId,
  tagIds,
  clientId,
  timeZone,
  initialFilter,
  onFilterChange,
}: {
  patrolId: number
  siteId: number
  tagIds: number[]
  clientId?: number
  timeZone?: string
  initialFilter: ScanFilterValue
  onFilterChange: (v: ScanFilterValue) => void
}) {
  const [pageNumber, setPageNumber] = useState(1)
  const scans = usePatrolScans(patrolId, {
    ...initialFilter,
    pageNumber,
    pageSize: PAGE_SIZE,
  })
  const guards = useGuardLookup(clientId)
  const tags = usePatrolTags(siteId, tagIds)
  const guardOptions = useMemo(
    () =>
      (guards.data ?? []).map((g) => ({
        guardUserId: g.id,
        label: `${g.firstName} ${g.lastName}`.trim(),
      })),
    [guards.data],
  )
  const totalPages = scans.data?.totalPages ?? 1

  return (
    <div className="space-y-3">
      <ScanFilters
        guards={guardOptions}
        tags={tags.options}
        value={initialFilter}
        onChange={(v) => {
          setPageNumber(1)
          onFilterChange(v)
        }}
      />
      <ScanList
        scans={scans.data?.items ?? []}
        isLoading={scans.isLoading}
        emptyMessage="No scans recorded for this patrol."
        timeZone={timeZone}
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={pageNumber}
          totalPages={totalPages}
          onPageChange={setPageNumber}
          hasNextPage={pageNumber < totalPages}
          hasPreviousPage={pageNumber > 1}
        />
      )}
    </div>
  )
}

function PatrolCheckpoints({
  siteId,
  tagIds,
}: {
  siteId: number
  tagIds: number[]
}) {
  const tags = usePatrolTags(siteId, tagIds)
  if (tagIds.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No checkpoints assigned.</p>
    )
  }
  if (tags.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }
  return (
    <ul className="space-y-2 text-sm">
      {tagIds.map((tid) => {
        const tag = tags.byId.get(tid)
        return (
          <li key={tid}>
            <div className="font-medium">
              {tag?.name ?? 'Checkpoint no longer available'}
            </div>
            {tag && (
              <div className="text-xs text-muted-foreground">
                {tag.tagType === 'NFC' ? 'NFC tag' : 'QR code'}
                {tag.locationDescription && ` · ${tag.locationDescription}`}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
